import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Download, Brain, BarChart3, CheckCircle, AlertCircle, RefreshCw, 
  TrendingUp, TrendingDown, Users, Star, Search, Zap, Shield, Award
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebaseConfig';
import { Helmet } from 'react-helmet-async';
import { FaApple, FaAndroid } from 'react-icons/fa';
import { trackEvent } from '../lib/analytics';

const Home = () => {
  const [appId, setAppId] = useState('');
  const [storeType, setStoreType] = useState('apple');
  const [detectedStore, setDetectedStore] = useState(null);
  const [normalizedFromUrl, setNormalizedFromUrl] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentJob, setCurrentJob] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [isSimulating, setIsSimulating] = useState(false);
  const navigate = useNavigate();

  const detectStoreFromInput = (rawInput) => {
    if (!rawInput || typeof rawInput !== 'string') return null;
    const input = rawInput.trim();
    if (input.startsWith('http://') || input.startsWith('https://')) {
      try {
        const url = new URL(input);
        const host = url.hostname.toLowerCase();
        if (host.includes('play.google.com')) {
          const pkg = url.searchParams.get('id');
          if (pkg && /^[A-Za-z][A-Za-z0-9_]*(\.[A-Za-z0-9_]+)+$/.test(pkg)) return { appId: pkg, storeType: 'google', reason: 'google_url' };
        }
        if (host.includes('apps.apple.com')) {
          const match = url.pathname.match(/id(\d{5,})/i);
          if (match && match[1]) return { appId: match[1], storeType: 'apple', reason: 'apple_url' };
        }
      } catch (e) {}
    }
    if (/^\d{5,}$/.test(input)) return { appId: input, storeType: 'apple', reason: 'numeric_id' };
    if (/^[A-Za-z][A-Za-z0-9_]*(\.[A-Za-z0-9_]+)+$/.test(input)) return { appId: input, storeType: 'google', reason: 'android_package' };
    const appleIdMatch = input.match(/id(\d{5,})/i);
    if (appleIdMatch && appleIdMatch[1]) return { appId: appleIdMatch[1], storeType: 'apple', reason: 'apple_id_in_text' };
    return null;
  };

  const getStatusMessage = (status) => {
    switch (status) {
      case 'pending': return 'Starting analysis...';
      case 'fetching_reviews': return 'Fetching app reviews...';
      case 'analyzing': return 'Analyzing with AI...';
      case 'completed': return 'Analysis completed!';
      case 'failed': return 'Analysis failed';
      default: return 'Processing...';
    }
  };

  const resetForm = () => {
    setError('');
    setIsLoading(false);
    setCurrentJob(null);
    setJobStatus(null);
  };

  // Poll job status
  useEffect(() => {
    let pollInterval;
    if (!isSimulating && currentJob && jobStatus?.status && !['completed', 'failed'].includes(jobStatus.status)) {
      pollInterval = setInterval(async () => {
        try {
          const getJobStatusFn = httpsCallable(functions, 'getJobStatus');
          const response = await getJobStatusFn({ jobId: currentJob });
          if (response.data.success) {
            setJobStatus(response.data.data);
            if (response.data.data.status === 'completed') {
              setTimeout(() => navigate(`/analysis/${response.data.data.appId}`), 1000);
            }
            if (response.data.data.status === 'failed') {
              setError(response.data.data.error || 'Job failed');
              setIsLoading(false);
              setCurrentJob(null);
              setJobStatus(null);
            }
          }
        } catch (error) {
          console.error('Error polling job status:', error);
        }
      }, 2000);
    }
    return () => { if (pollInterval) clearInterval(pollInterval); };
  }, [currentJob, jobStatus?.status, navigate, isSimulating]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!appId.trim()) { setError('Please enter an App ID'); return; }
    trackEvent('start_ai_analysis', { app_id_input: appId.trim(), platform_selected: storeType });
    resetForm();
    setIsLoading(true);

    try {
      const submitAnalysisJob = httpsCallable(functions, 'submitAnalysisJob');
      const response = await submitAnalysisJob({
        appId: appId.trim(),
        storeType: 'auto',
        usePagination: true,
        pageSize: storeType === 'google' ? 100 : undefined,
      });

      if (response.data.success) {
        const job = response.data.data;
        setCurrentJob(job.jobId);
        setJobStatus(job);

        if (job.status === 'completed') {
          setIsSimulating(true);
          const targetMs = 3000 + Math.floor(Math.random() * 1000);
          const start = Date.now();
          const tick = () => {
            const elapsed = Date.now() - start;
            const simulated = Math.min(100, Math.floor((elapsed / targetMs) * 100));
            setJobStatus(prev => ({ ...(prev || job), status: 'analyzing', progress: Math.max(prev?.progress || 0, simulated), message: 'Preparing analysis...' }));
            if (elapsed >= targetMs) {
              setIsSimulating(false);
              setIsLoading(false);
              navigate(`/analysis/${job.appId}`);
            } else {
              requestAnimationFrame(tick);
            }
          };
          requestAnimationFrame(tick);
          return;
        }
      } else {
        setError('Failed to start analysis');
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error starting analysis:', error);
      setError(error.message || 'An error occurred. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <Helmet>
        <title>AppReview.ai — Turn App Reviews Into Weekly Action Plans</title>
        <meta name="description" content="Analyze Apple App Store and Google Play reviews with AI. Prioritize the issues hurting rating and retention, then ship fixes with a clear action plan." />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href="https://appreview.ai/" />
        <meta property="og:url" content="https://appreview.ai/" />
        <meta property="og:title" content="AppReview.ai — Turn Reviews Into Weekly Product Decisions" />
        <meta property="og:description" content="Find the issues hurting rating and retention, prioritize what to fix, and ship faster with AI-powered review analysis." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AppReview.ai — Turn Reviews Into Weekly Product Decisions" />
        <meta name="twitter:description" content="Prioritize the issues that matter and turn feedback into a clear action plan." />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "AppReview.ai",
          "url": "https://appreview.ai",
          "description": "AI-powered app store review analysis tool. Analyze Apple App Store and Google Play reviews to uncover sentiment, feature requests, and actionable insights.",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web",
          "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD", "description": "Free tier available" },
          "creator": { "@type": "Organization", "name": "AppReview.ai", "url": "https://appreview.ai" }
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How does AppReview.ai analyze app store reviews?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "AppReview.ai uses AI to automatically fetch and analyze reviews from the Apple App Store and Google Play. It identifies sentiment patterns, top user complaints, feature requests, and strengths — then delivers a prioritized action plan you can use to improve your app."
              }
            },
            {
              "@type": "Question",
              "name": "What app stores are supported?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "AppReview.ai supports both the Apple App Store and Google Play Store. Simply paste an app URL or enter an app ID, and the tool automatically detects which store to pull reviews from."
              }
            },
            {
              "@type": "Question",
              "name": "Is AppReview.ai free to use?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, AppReview.ai offers a free tier that lets you analyze app reviews without any upfront cost. Additional analysis credits are available for teams that need higher volume or deeper insights."
              }
            },
            {
              "@type": "Question",
              "name": "How is this different from reading reviews manually?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Manual review reading is slow and biased — you tend to notice recent or extreme reviews. AppReview.ai processes hundreds of reviews at once, quantifies sentiment trends, clusters related feedback into themes, and surfaces the issues most likely to impact your rating and retention."
              }
            },
            {
              "@type": "Question",
              "name": "Can I export the analysis results?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, you can export your full analysis report as a PDF, making it easy to share with your product team, stakeholders, or include in sprint planning documents."
              }
            }
          ]
        })}</script>
      </Helmet>
      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-purple-600/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-12 lg:pt-16 pb-12 sm:pb-16 lg:pb-24">
          <div className="text-center">
            <div className="inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-blue-100 text-blue-800 text-xs sm:text-sm font-medium mb-4 sm:mb-6">
              <Zap className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />AI-Powered Weekly Insight
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight px-2">
              Turn App Reviews Into<span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"> This Week’s Action Plan</span>
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 mb-3 sm:mb-4 max-w-3xl mx-auto leading-relaxed px-4">
              AppReview.ai analyzes App Store and Google Play reviews, surfaces what hurts rating and retention, and prioritizes exactly what to fix next.
            </p>
            <p className="text-sm sm:text-base text-gray-500 mb-6 sm:mb-8">No credit card required · Results in minutes · iOS + Android</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              <a href="#analysis-form" className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold hover:from-blue-700 hover:to-purple-700 transition-all">Analyze an App for Free</a>
              <a href="#sample-results" className="inline-flex items-center justify-center px-6 py-3 rounded-xl border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-all">View Sample Results</a>
            </div>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 sm:-mt-8 lg:-mt-12 mb-8 sm:mb-12 lg:mb-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-lg sm:rounded-xl flex items-center justify-center mb-4 sm:mb-6"><Download className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-white" /></div>
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">See What to Fix First</h3>
            <p className="text-sm sm:text-base text-gray-600 leading-relaxed">Automatically cluster feedback and rank the highest-impact issues affecting rating and retention.</p>
          </div>
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-lg sm:rounded-xl flex items-center justify-center mb-4 sm:mb-6"><Brain className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-white" /></div>
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">Turn Sentiment Into Action</h3>
            <p className="text-sm sm:text-base text-gray-600 leading-relaxed">Every insight includes user evidence, impact level, and clear next steps for PM, engineering, and support.</p>
          </div>
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
            <div className="bg-gradient-to-r from-purple-500 to-purple-600 w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-lg sm:rounded-xl flex items-center justify-center mb-4 sm:mb-6"><BarChart3 className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-white" /></div>
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">Measure Weekly Impact</h3>
            <p className="text-sm sm:text-base text-gray-600 leading-relaxed">Track whether shipped fixes are working with trends across sentiment, topics, and ratings.</p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div id="analysis-form" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-8 sm:mb-12 lg:mb-16">
        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
            <div className="flex items-center"><Search className="w-5 h-5 sm:w-6 sm:h-6 text-white mr-2 sm:mr-3" /><h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">Analyze Your App for Free</h2></div>
          </div>
          <div className="p-4 sm:p-6 lg:p-8">
            <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
              <div>
                <label htmlFor="appId" className="block text-sm font-semibold text-gray-900 mb-3">App Identifier</label>
                <input
                  type="text" id="appId" value={appId}
                  onChange={(e) => {
                    const value = e.target.value;
                    setAppId(value);
                    setNormalizedFromUrl(false);
                    const detected = detectStoreFromInput(value);
                    if (detected) {
                      setDetectedStore(detected.storeType);
                      if (detected.appId && detected.appId !== value) { setAppId(detected.appId); setNormalizedFromUrl(true); }
                      if (detected.storeType !== storeType) setStoreType(detected.storeType);
                    } else { setDetectedStore(null); }
                  }}
                  placeholder="Enter App Store ID or Google Play Package Name"
                  className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl text-lg focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 transition-all duration-200"
                  disabled={isLoading}
                />
                <div className="mt-3 p-3 sm:p-4 bg-gray-50 rounded-lg">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600">
                    <div className="flex items-center"><FaApple className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2 text-gray-900" /><span><strong>Apple:</strong> Numeric ID (e.g., 284882215)</span></div>
                    <div className="flex items-center"><FaAndroid className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2 text-green-600" /><span><strong>Google:</strong> Package name (e.g., com.facebook.katana)</span></div>
                  </div>
                  {detectedStore && (
                    <div className="mt-3 text-sm">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full font-medium ${detectedStore === 'apple' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`}>
                        {detectedStore === 'apple' ? 'Detected: Apple App Store' : 'Detected: Google Play Store'}
                      </span>
                      {normalizedFromUrl && <span className="ml-2 text-gray-500">(Normalized ID from URL)</span>}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-3 sm:mb-4">Platform Selection</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <label className={`relative flex items-center p-3 sm:p-4 border-2 rounded-lg sm:rounded-xl cursor-pointer transition-all duration-200 ${storeType === 'apple' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                    <input type="radio" name="storeType" value="apple" checked={storeType === 'apple'} onChange={(e) => setStoreType(e.target.value)} className="sr-only" disabled={isLoading} />
                    <div className="flex items-center flex-1"><div className="bg-black p-1.5 sm:p-2 rounded-lg mr-2 sm:mr-3"><FaApple className="w-4 h-4 sm:w-5 sm:h-5 text-white" /></div><div className="flex-1 min-w-0"><div className="font-semibold text-gray-900 text-sm sm:text-base">Apple App Store</div><div className="text-xs sm:text-sm text-gray-500">iOS applications</div></div></div>
                    {storeType === 'apple' && <CheckCircle className="w-5 h-5 text-blue-500 ml-2" />}
                  </label>
                  <label className={`relative flex items-center p-3 sm:p-4 border-2 rounded-lg sm:rounded-xl cursor-pointer transition-all duration-200 ${storeType === 'google' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'}`}>
                    <input type="radio" name="storeType" value="google" checked={storeType === 'google'} onChange={(e) => setStoreType(e.target.value)} className="sr-only" disabled={isLoading} />
                    <div className="flex items-center flex-1"><div className="bg-green-600 p-1.5 sm:p-2 rounded-lg mr-2 sm:mr-3"><FaAndroid className="w-4 h-4 sm:w-5 sm:h-5 text-white" /></div><div className="flex-1 min-w-0"><div className="font-semibold text-gray-900 text-sm sm:text-base">Google Play Store</div><div className="text-xs sm:text-sm text-gray-500">Android applications</div></div></div>
                    {storeType === 'google' && <CheckCircle className="w-5 h-5 text-green-500 ml-2" />}
                  </label>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border-l-4 border-red-400 rounded-r-xl p-6">
                  <div className="flex items-start"><AlertCircle className="w-6 h-6 text-red-400 mr-3 mt-0.5 flex-shrink-0" /><div className="flex-1"><h3 className="font-semibold text-red-800">Analysis Error</h3><p className="text-red-700 mt-1">{error}</p><button type="button" onClick={resetForm} className="mt-3 text-sm font-medium text-red-600 hover:text-red-800 underline">Try Again</button></div></div>
                </div>
              )}

              {isLoading && jobStatus && (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-6">
                  <div className="flex items-center mb-4">
                    <div className="flex items-center mr-4">{jobStatus.status === 'completed' ? <CheckCircle className="w-8 h-8 text-green-500" /> : <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />}</div>
                    <div className="flex-1"><h3 className="font-bold text-blue-900 text-lg">{getStatusMessage(jobStatus.status)}</h3><p className="text-blue-700 text-sm">{jobStatus.message}</p></div>
                    <div className="text-right"><div className="text-2xl font-bold text-blue-600">{typeof jobStatus.progress === 'number' ? Math.max(1, jobStatus.progress) : 1}%</div><div className="text-xs text-blue-500">Complete</div></div>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-3 overflow-hidden">
                    <div className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-500 ease-out" style={{ width: `${typeof jobStatus.progress === 'number' ? Math.max(1, Math.min(100, jobStatus.progress)) : 1}%` }}></div>
                  </div>
                </div>
              )}

              <button type="submit" disabled={isLoading} className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold py-3 sm:py-4 px-6 sm:px-8 rounded-lg sm:rounded-xl text-base sm:text-lg transition-all duration-200 transform hover:scale-105 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none">
                {isLoading ? (
                  <div className="flex items-center justify-center"><RefreshCw className="w-5 h-5 mr-3 animate-spin" /><span>{jobStatus?.status === 'completed' ? 'Redirecting to Results...' : 'Processing Analysis...'}</span></div>
                ) : (
                  <div className="flex items-center justify-center"><Search className="w-5 h-5 mr-3" /><span>Analyze App for Free</span></div>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Preview section */}
      <div id="sample-results" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8 sm:mb-12 lg:mb-16">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 px-4">What You Get in Every Report</h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">Prioritized issues, recurring requests, and clear action plans your team can execute.</p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300">
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
              <div className="flex items-center"><TrendingUp className="w-6 h-6 sm:w-8 sm:h-8 text-white mr-3 sm:mr-4" /><div><h3 className="text-lg sm:text-2xl font-bold text-white">What Drives Growth</h3><p className="text-green-100 text-sm sm:text-base">Signals to protect and amplify</p></div></div>
            </div>
            <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
              {[{icon: Star, color: 'text-yellow-500', title: 'Top Loved Features', desc: 'Identify what users consistently praise'},
                {icon: Users, color: 'text-blue-500', title: 'Experience Highlights', desc: 'Spot workflows that keep users happy'},
                {icon: Award, color: 'text-purple-500', title: 'Differentiators', desc: 'Understand what sets your app apart'},
                {icon: Shield, color: 'text-green-500', title: 'Keep/Scale Recommendations', desc: 'What to preserve and double down on'}
              ].map((item, i) => (
                <div key={i} className="flex items-start"><item.icon className={`w-5 h-5 sm:w-6 sm:h-6 ${item.color} mr-2 sm:mr-3 mt-1 flex-shrink-0`} /><div><h4 className="font-semibold text-gray-900 mb-1 sm:mb-2 text-sm sm:text-base">{item.title}</h4><p className="text-gray-600 text-xs sm:text-sm">{item.desc}</p></div></div>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300">
            <div className="bg-gradient-to-r from-red-500 to-pink-600 px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
              <div className="flex items-center"><TrendingDown className="w-6 h-6 sm:w-8 sm:h-8 text-white mr-3 sm:mr-4" /><div><h3 className="text-lg sm:text-2xl font-bold text-white">What to Fix First</h3><p className="text-red-100 text-sm sm:text-base">Highest-impact problems to resolve</p></div></div>
            </div>
            <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
              {[{icon: AlertCircle, color: 'text-red-500', title: 'P0 Issue List', desc: 'Top risks hurting rating and retention'},
                {icon: BarChart3, color: 'text-orange-500', title: 'High-Frequency Pain Points', desc: 'Themes repeatedly mentioned by users'},
                {icon: Zap, color: 'text-blue-500', title: 'Recommended Fixes', desc: 'Specific changes your team can ship'},
                {icon: CheckCircle, color: 'text-green-500', title: '1-Day / 1-Week / 1-Month Plan', desc: 'Prioritized execution roadmap'}
              ].map((item, i) => (
                <div key={i} className="flex items-start"><item.icon className={`w-5 h-5 sm:w-6 sm:h-6 ${item.color} mr-2 sm:mr-3 mt-1 flex-shrink-0`} /><div><h4 className="font-semibold text-gray-900 mb-1 sm:mb-2 text-sm sm:text-base">{item.title}</h4><p className="text-gray-600 text-xs sm:text-sm">{item.desc}</p></div></div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Who it's for */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8 sm:mb-12 lg:mb-16">
        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl p-6 sm:p-8 lg:p-10">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 text-center">Built for teams that need decisions, not more dashboards</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
            <div className="p-4 rounded-xl bg-blue-50 border border-blue-100">
              <h3 className="font-semibold text-gray-900 mb-2">Indie Developers</h3>
              <p className="text-sm text-gray-700">Save hours each week by skipping manual review triage.</p>
            </div>
            <div className="p-4 rounded-xl bg-purple-50 border border-purple-100">
              <h3 className="font-semibold text-gray-900 mb-2">Product Managers</h3>
              <p className="text-sm text-gray-700">Align roadmap priorities with real user pain—fast.</p>
            </div>
            <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-100">
              <h3 className="font-semibold text-gray-900 mb-2">Support & Ops Teams</h3>
              <p className="text-sm text-gray-700">Catch repeated complaints early and close the loop faster.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing teaser */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 sm:pb-16 lg:pb-20">
        <div className="text-center bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl sm:rounded-3xl p-8 sm:p-10 text-white shadow-2xl">
          <h2 className="text-2xl sm:text-3xl font-bold mb-3">Start free. Upgrade when it proves value.</h2>
          <p className="text-gray-200 mb-6 max-w-2xl mx-auto">Free plan includes one app analysis and a baseline summary. Upgrade to unlock history, exports, and recurring intelligence reports.</p>
          <a href="#analysis-form" className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-white text-gray-900 font-semibold hover:bg-gray-100 transition-all">Start Free Analysis</a>
        </div>
      </div>
    </div>
  );
};

export default Home;
